import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../../../context/UserProvider'; 
import './CallbackRequestPage.css';
import { fetchAuthSession } from 'aws-amplify/auth';
import ReactLoading from 'react-loading';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/navbars/Navbar';
import { FaPhone } from "react-icons/fa6";

export default function CallBackRequestPage() {
    const navigate = useNavigate();
    const { mongoUser, cognitoUser } = useUser();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [noteInputs, setNoteInputs] = useState({}); // Store note inputs
    const [showNoteInputs, setShowNoteInputs] = useState({}); // Store which notes are visible


    const handleNoteChange = (callbackId, value) => {
        setNoteInputs(prev => ({ ...prev, [callbackId]: value }));
    };
    // Handle status change
    const handleStatusChange = async (callbackId, newStatus) => {
            try {
                const session = await fetchAuthSession();
                const idToken = session.tokens?.idToken?.toString();
        
                if (!callbackId) {
                    console.error("❌ No callbackId provided!");
                    return;
                }
        
                // ✅ Log the payload before sending
                const requestBody = { callbackId, newStatus };
                console.log("📡 Sending PUT Request:", requestBody);
        
                const response = await axios.put(
                    `${import.meta.env.VITE_REQUEST_CALLBACK}/request-callback/${callbackId}`,
                    requestBody,  // ✅ Ensure callbackId is included
                    {
                        headers: { 
                            Authorization: `Bearer ${idToken}`,
                            "Content-Type": "application/json"
                        }
                    }
                );
        
                setRequests(prevRequests =>
                    prevRequests.map(req => req._id === callbackId ? { ...req, currentStatus: newStatus } : req)
                );
        
                console.log("✅ Status updated successfully:", response.data);
            } catch (err) {
                console.error("❌ Error updating status:", err.response?.data || err);
            }
        };
    

    // Handle note input changes
    const handleNoteSubmit = async (callbackId) => {
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            const note = noteInputs[callbackId];
    
            if (!note) {
                console.warn("⚠ No note provided!");
                return;
            }
    
            const response = await axios.put(
                `${import.meta.env.VITE_REQUEST_CALLBACK}/request-callback/${callbackId}/add-note`,
                { note },
                {
                    headers: { Authorization: `Bearer ${idToken}` },
                    "Content-Type": "application/json"
                }
            );
    
            // ✅ Update the callback request with the latest data from the response
            setRequests(prevRequests =>
                prevRequests.map(req => 
                    req._id === callbackId ? response.data.callback : req
                )
            );
    
            console.log("✅ Note added successfully:", response.data);
    
            // ✅ Clear input after submission
            setNoteInputs(prev => ({ ...prev, [callbackId]: "" }));
            setShowNoteInputs(prev => ({ ...prev, [callbackId]: false }));
        } catch (err) {
            console.error("❌ Error submitting note:", err);
        }
    };
    

    useEffect(() => {
        if (!mongoUser) {
            navigate('/login');
            return;
        }
    }, [mongoUser, navigate]);

    useEffect(() => {
        if (!mongoUser || !mongoUser._id || !cognitoUser) {
            console.warn("mongoUser or cognitoUser not available, skipping API call.");
            return;
        }
    
        setLoading(true);
    
        const getData = async () => {
            try {
                if (mongoUser.role !== "Admin" && mongoUser.role !== "Employee") {
                    console.error("❌ Access Denied: User is not authorized.");
                    setLoading(false);
                    return;
                }
                
                console.log("Fetching requests for user:", mongoUser._id);
                
                const session = await fetchAuthSession();
                const idToken = session.tokens.idToken.toString();
                const response = await axios.get(`${import.meta.env.VITE_REQUEST_CALLBACK}/request-callback/admin`, {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        "Content-Type": "application/json"
                    },
                });
                console.log("🔹 API Response Data:", response.data);
                const data = response.data.callbacks;
                setRequests(data);
            } catch (err) {
                setError("Failed to fetch callback requests.");
                console.error("❌ Error fetching data:", err);
            } finally {
                setLoading(false);
            }
        };
    
        getData();
    }, [mongoUser, cognitoUser]);

    return (
        <>
            <Navbar/>
        
            { loading ? (
                <ReactLoading width={80} height={80} color="dodgerblue" type='spin'/>
            ) : (
                <div className="callback-container-page">
                    <h2>Callback Requests</h2>
                    <div className='cardContainer'>
                    {Array.isArray(requests) && requests.length > 0 ? (
                        requests.map((request) => {
                            const callbackId = request._id; // ✅ Rename request._id to callbackId
                            
                            return (
                                <div key={callbackId} className="callback-card">
                                    <div className="callback-header">
                                        <h3>{request.firstName} {request.lastName}</h3>
                                        <p className="email">{request.email}</p>
                                    </div>
                                    <div className="call-button-container">
                                        <a href={`tel:${request.phoneNumber}`} className="call-button">
                                            <FaPhone />
                                            Call {request.phoneNumber}
                                        </a>
                                    </div>
                                    <p className='message'><strong>Message:</strong> {request.message}</p>
                                    <p><strong>Requested On:</strong> {new Date(request.date).toLocaleString()}</p>
                                    
                                    {/* Status Dropdown */}
                                    <div className="status-container">
                                        <label htmlFor={`status-${callbackId}`}>Status:</label>
                                        <select
                                            id={`status-${callbackId}`}
                                            value={request.currentStatus}
                                            onChange={(e) => handleStatusChange(callbackId, e.target.value)}
                                        >
                                            <option value="New">New</option>
                                            <option value="No-Answer">No-Answer</option>
                                            <option value="Completed">Completed</option>
                                            <option value="Escalated">Escalated</option>
                                            <option value="Follow-up-Required">Follow-up Required</option>
                                        </select>
                                    </div>

                                    {/* Notes Section */}
                                    <div className='notesArea'>
                                        {request.notes?.map((item, key) => (
                                            <div className='noteItem' key={key}>
                                                <p>{item.note}</p>
                                                <p>{new Date(request.date).toLocaleString()}</p>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Add Note Button */}
                                    <button 
                                        className="add-note-button" 
                                        onClick={() => setShowNoteInputs(prev => ({ ...prev, [callbackId]: !prev[callbackId] }))}
                                    >
                                        {showNoteInputs[callbackId] ? "Cancel" : "Add Note"}
                                    </button>

                                    {/* Note Input Field */}
                                    {showNoteInputs[callbackId] && (
                                        <div className="note-input-container">
                                            <textarea
                                                value={noteInputs[callbackId] || ""}
                                                onChange={(e) => handleNoteChange(callbackId, e.target.value)}
                                                placeholder="Enter your note..."
                                            />
                                            <button className="button2" onClick={() => handleNoteSubmit(callbackId)}>
                                                Submit Note
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <p>No callback requests found.</p>
                    )}
                    </div>
                </div>
            )}
        </>
    );
}
